import React, { useState, useEffect } from 'react';
import { CiFilter } from 'react-icons/ci';
import { LiaSortSolid } from "react-icons/lia";
import '../Filters/Filters.scss';
import SearchFilter from './SearchFilter';
import MultiSelectFilter from './MultiSelectFilter';
import FilterChip from './FilterChip';
import SavedSearchFilter from './SavedSearchFilter';
import { AlertModal, SaveSearchModal } from '../Modal';
import BasicTooltip from '../Tooltip/BasicTooltip';
import { FILTER_ICON, REPO_ICON } from '../Svgs';
import { getFieldByName } from '../../utils';
import { createNamedQuery, deleteNamedQuery, getMaxNamedQueries } from '../../services';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { buildSearchCriteria } from '../../utils/filters/buildSearchCriteria';
import { applySearchCriteria } from '../../utils/filters/applySearchCriteria';
import CustomFilterPopover from './CustomFilterPopover';
import SortPopover from './SortPopover';
import { generateFilterChips, computeIntersectionSortableFields } from '../../utils/filters/helpers';
import CustomTooltip from '../Tooltip/CustomTooltip';
import { FaSortUp, FaSortDown } from "react-icons/fa6";

const CustomFilters = ({
  searchValue,
  onSearchChange,
  repoOptions,
  selectedRepos,
  onSelectedReposChange,
  repoFilters,
  onRepoFilterChange,
  onReset,
  onApply,
  onSaveAndApply,
  savedSearchValue,
  onSavedSearchChange,
  searchLayoutFields,
  canApply = false,
  setShowResults,
  filtersConditions,
  setFiltersConditions,
  sortField,
  sortDirection,
  onSortChange
}) => {
  const [showFieldPopover, setShowFieldPopover] = useState(false);
  const [expandedFieldKey, setExpandedFieldKey] = useState(null);
  const [showSaveSearchModal, setShowSaveSearchModal] = useState(false);
  const [refreshSavedSearches, setRefreshSavedSearches] = useState(Date.now());
  const [selectedSearchData, setSelectedSearchData] = useState(null);
  const [maxNamedQueries, setMaxNamedQueries] = useState(null);
  const [savedQueriesCount, setSavedQueriesCount] = useState(0);
  const includesSearchField = getFieldByName(searchLayoutFields, "text") || false;
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [searchToDelete, setSearchToDelete] = useState(null);
  const [showSortPopover, setShowSortPopover] = useState(false);
  const [expandedSortFieldKey, setExpandedSortFieldKey] = useState(null);
  const filteredFilterFields = searchLayoutFields?.filter((field) => field?.fieldName !== 'text');
  const contextureMetadata = useSelector((state) => state.contextureMetadata);

  // Compute common sortable fields across selected repositories
  const selectedRepoIds = selectedRepos?.map((r) => r.value) || [];
  const commonSortableFields = computeIntersectionSortableFields(
    contextureMetadata?.repos,
    selectedRepoIds
  );

  useEffect(() => {
    const fetchQueriesData = async () => {
      try {
        const [maxResponse] = await getMaxNamedQueries();
        setMaxNamedQueries(maxResponse?.maxNamedQueries || 5);
      } catch (error) {
        setMaxNamedQueries(5);
      }
    };

    fetchQueriesData();
  }, []);

  // Clear repoFilters when repos change and validate field support
  useEffect(() => {
    if (repoFilters && Object.keys(repoFilters).length > 0) {
      const newRepoFilters = { ...repoFilters };
      let hasChanges = false;

      Object.keys(repoFilters).forEach(fieldName => {
        // Handle date range fields (fieldNameStart, fieldNameEnd)
        if (fieldName.endsWith('Start') || fieldName.endsWith('End')) {
          const baseFieldName = fieldName.replace(/Start|End$/, '');
          const field = searchLayoutFields?.find(f => f.fieldName === baseFieldName);

          // Clear date range filter if base field is not supported by new repos
          if (!field) {
            delete newRepoFilters[fieldName];
            hasChanges = true;
          }
        }
        // Handle relative date filter fields (fieldNameRelDate, fieldNameRelDateVal)
        else if (fieldName.endsWith('RelDateVal')) {
          const baseFieldName = fieldName.replace(/RelDateVal$/, '');
          const field = searchLayoutFields?.find(f => f.fieldName === baseFieldName);

          // Clear relative date filter if base field is not supported by new repos
          if (!field) {
            delete newRepoFilters[fieldName];
            hasChanges = true;
          }
        }
        else {
          const field = searchLayoutFields?.find(f => f.fieldName === fieldName);

          // Clear filter if field is not supported by new repos
          if (!field) {
            delete newRepoFilters[fieldName];
            hasChanges = true;
          } else if (field.validValues && Array.isArray(field.validValues)) {
            // Check if selected value exists in new valid values
            const currentValue = repoFilters[fieldName];
            const currentValueKey = typeof currentValue === 'string' ? currentValue : currentValue?.value;

            if (currentValueKey) {
              const valueExists = field.validValues.some(v => {
                const validValueKey = typeof v === 'string' ? v : v.value;
                return validValueKey === currentValueKey;
              });

              if (!valueExists) {
                delete newRepoFilters[fieldName];
                hasChanges = true;
              }
            }
          }
        }
      });

      if (hasChanges && onRepoFilterChange) {
        onRepoFilterChange(null, null, true); // Reset all filters
        // Re-apply valid filters
        Object.entries(newRepoFilters).forEach(([fieldName, value]) => {
          onRepoFilterChange(fieldName, value);
        });
      }
    }
  }, [selectedRepos, searchLayoutFields, onRepoFilterChange, repoFilters]);



  const filterChips = generateFilterChips({
    fields: filteredFilterFields,
    filters: repoFilters,
    filtersConditions,
    FilterChange: onRepoFilterChange
  });

  // Generate sort chips
  const sortChips = [];
  if (sortField && sortDirection) {
    // Handle both single sort (backward compatibility) and multiple sorts
    const sortFields = Array.isArray(sortField) ? sortField : [{ field: sortField, direction: sortDirection }];

    sortFields.forEach((sortItem, index) => {
      const sortFieldObj = commonSortableFields.find(f => f.fieldName === sortItem.field);
      if (sortFieldObj) {
        const getFieldDisplayName = (field) => {
          return field.displayName || field.label || field.name || field.fieldName;
        };
        sortChips.push({
          key: sortItem.field,
          label: getFieldDisplayName(sortFieldObj),
          value: `${getFieldDisplayName(sortFieldObj)} (${sortItem.direction === 'ASC' ? 'ASC' : 'DESC'})`,
          icon: sortItem.direction === 'ASC' ? <FaSortUp /> : <FaSortDown />
        });
      }
    });
  }

  const hasAnyFieldOptions = filteredFilterFields.length > 0;

  const isMaxQueriesReached = maxNamedQueries !== null && savedQueriesCount >= maxNamedQueries;
  const isSaveDisabled = !canApply || (savedSearchValue === 'new' && isMaxQueriesReached);

  const handleSaveSearchModal = () => {
    setShowSaveSearchModal(true);
  };

  const handleSavedSearchChange = (value, data) => {
    setSelectedSearchData(data);
    onSavedSearchChange?.(value, data);
  };

  const handleApplySavedSearch = ({ searchCriteria, repositoryIds, sorts }) => {

    // Only reset filters if it's not "New Search" selection
    if (searchCriteria?.children?.length > 0) {
      onReset?.();
    }

    if (searchCriteria?.children?.length === 0) {
      onSelectedReposChange?.(repoOptions);
      onSearchChange?.('');
      onRepoFilterChange?.(null, null, true);
      onSortChange?.(null, null); // Clear sorting
      setShowResults(false);
      return;
    }

    if (repositoryIds?.length) {
      const selectedRepos = repoOptions.filter(repo =>
        repositoryIds.includes(repo.value)
      );
      onSelectedReposChange?.(selectedRepos);
    }


    if (searchCriteria && searchCriteria.children) {
      applySearchCriteria({
        searchCriteria,
        repositoryIds,
        contextureMetadata,
        onSearchChange,
        onRepoFilterChange,
        setFiltersConditions,
        sorts,
        onSortChange,
      });
    }

    onApply?.();
  };

  const handleSaveSearch = async (searchName) => {
    try {

      const searchCriteria = buildSearchCriteria({
        searchValue,
        repoFilters,
        filtersConditions,
      });

      const namedQueryData = {
        name: searchName,
        repositoryIds: selectedRepos ? selectedRepos.map(repo => repo.value) : [],
        searchCriteria,
        sorts: sortField?.length ? sortField.map(sort => ({
          fieldName: sort.field,
          direction: sort.direction
        })) : []
      };

      let newSearchValue = searchName;

      // Check if this is an update or create operation
      if (savedSearchValue && savedSearchValue !== 'new') {
        // Check if the name has changed
        const oldSearchData = selectedSearchData;
        const nameChanged = oldSearchData?.label !== searchName;

        if (nameChanged) {
          // User changed the name - delete old query and create new one
          await deleteNamedQuery(savedSearchValue);
          await createNamedQuery(namedQueryData);
          newSearchValue = searchName; // Use the new name
          toast.success(`Search saved as "${searchName}" successfully!`, {
            position: 'top-right',
            autoClose: 3000,
          });
        } else {
          // User kept the same name - update existing query
          await createNamedQuery(namedQueryData);
          newSearchValue = searchName; // Keep the same name
          toast.success(`Search "${searchName}" updated successfully!`, {
            position: 'top-right',
            autoClose: 3000,
          });
        }
      } else {
        // Create new query
        await createNamedQuery(namedQueryData);
        newSearchValue = searchName; // Use the new name
        toast.success('Search saved successfully!', {
          position: 'top-right',
          autoClose: 3000,
        });
      }

      // Close modal after successful save
      setShowSaveSearchModal(false);

      // Trigger refresh of saved search list and select the new/updated search
      setRefreshSavedSearches(Date.now()); // Use timestamp for reliable trigger

      // Update selectedSearchData to reflect the new/updated search
      const updatedSearchData = { label: searchName, value: newSearchValue };
      setSelectedSearchData(updatedSearchData);
      onSavedSearchChange?.(newSearchValue, updatedSearchData);

      // Apply the search with the new/updated criteria
      if (onSaveAndApply) {
        // Apply the search immediately after saving
        onSaveAndApply();
      }
    } catch (error) {
      let errorMessage = 'Failed to save search. Please try again.';

      if (error.response?.data?.errorMessage) {
        errorMessage = error.response.data.errorMessage;
      }

      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 4000,
      });
    }
  };

  const handleDeleteSavedSearch = async (searchName) => {
    try {
      await deleteNamedQuery(searchName);
      toast.success('Search deleted successfully!', {
        position: 'top-right',
        autoClose: 3000,
      });

      // Add delay to ensure backend processes deletion before refreshing
      setTimeout(() => {
        // Reset to 'new' after deletion
        onSavedSearchChange?.('new');
        // Trigger refresh of saved searches list
        setRefreshSavedSearches(Date.now());
      }, 1500);
    } catch (error) {
      toast.error('Failed to delete search. Please try again.', {
        position: 'top-right',
        autoClose: 4000,
      });
      throw error;
    }
  };

  const handleDeleteConfirm = async () => {
    if (searchToDelete) {
      try {
        await handleDeleteSavedSearch(searchToDelete.value);
        setRefreshSavedSearches(Date.now());
        setShowDeleteAlert(false);
        setSearchToDelete(null);
        handleApplySavedSearch({
          searchCriteria: { children: [] }
        });
        handleSavedSearchChange('new');
        setShowResults(false);
      } catch (error) {
        console.error('Failed to delete saved search:', error);
      }
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteAlert(false);
    setSearchToDelete(null);
  };

  return (
    <div className="filters-panel">
      <div className="filters-row">
        <SavedSearchFilter
          value={savedSearchValue}
          onChange={handleSavedSearchChange}
          onDelete={handleDeleteSavedSearch}
          onRefreshNeeded={refreshSavedSearches}
          onApplySavedSearch={handleApplySavedSearch}
          setSavedQueriesCount={setSavedQueriesCount}
        />
        <MultiSelectFilter
          label="Repositories"
          options={repoOptions}
          value={selectedRepos}
          onChange={onSelectedReposChange}
          placeholder="Select repositories"
        />
        <div className='search-with-filters'>
          <SearchFilter value={searchValue} onChange={onSearchChange} disabled={!includesSearchField} />
          {hasAnyFieldOptions ? (
            <>
              <div className="position-relative">
                <CiFilter
                  style={{
                    fontSize: '38px',
                    marginTop: '22px',
                    cursor: 'pointer',
                    opacity: 1,
                  }}
                  onClick={() => {
                    setShowSortPopover(false);
                    setShowFieldPopover((open) => !open);
                  }}
                />
                {showFieldPopover && hasAnyFieldOptions && (
                  <CustomFilterPopover
                    setShowFieldPopover={setShowFieldPopover}
                    setExpandedFieldKey={setExpandedFieldKey}
                    expandedFieldKey={expandedFieldKey}
                    filteredFilterFields={filteredFilterFields}
                    repoFilters={repoFilters}
                    filtersConditions={filtersConditions}
                    onRepoFilterChange={onRepoFilterChange}
                    setFiltersConditions={setFiltersConditions}
                  />
                )}
              </div>
              <div className="position-relative">
                <LiaSortSolid
                  style={{
                    fontSize: '38px',
                    marginTop: '20px',
                    cursor: 'pointer',
                    opacity: 1,
                  }}
                  onClick={() => {
                    setShowFieldPopover(false);
                    setShowSortPopover((open) => !open);
                  }}
                />
                {showSortPopover && (
                  <SortPopover
                    setShowSortPopover={setShowSortPopover}
                    expandedSortFieldKey={expandedSortFieldKey}
                    setExpandedSortFieldKey={setExpandedSortFieldKey}
                    sortableFields={commonSortableFields}
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSortChange={onSortChange}
                  />
                )}
              </div>
            </>
          ) : (
            <>
              <CustomTooltip text={
                (!selectedRepos || selectedRepos.length === 0)
                  ? "Please select repositories to enable filtering options."
                  : "The repositories you've selected don't have any fields in common to filter on."
              }>
                <CiFilter
                  style={{
                    fontSize: '38px',
                    marginTop: '22px',
                    cursor: 'default',
                    opacity: 0.4,
                  }}
                />
              </CustomTooltip>
              <CustomTooltip text={
                (!selectedRepos || selectedRepos.length === 0)
                  ? "Please select repositories to enable sorting options."
                  : "The repositories you've selected don't have any fields in common to sort on."
              }>

                <LiaSortSolid
                  style={{
                    fontSize: '38px',
                    marginTop: '20px',
                    cursor: 'default',
                    opacity: 0.4,
                  }}
                />
              </CustomTooltip>

            </>
          )}
        </div>
      </div>

      {/* Repository chips */}
      {selectedRepos && selectedRepos.length > 0 && (<div className="repo-chips-container">
        <div className="filter-chips-header">
          <REPO_ICON width='18' height='18' />
          <span>Repositories: </span>
        </div>
        {selectedRepos.map((chip) => (
          <FilterChip
            key={chip?.value}
            label={''}
            value={chip?.label}
            onClear={() => {
              const newSelectedRepos = selectedRepos.filter(r => r.value !== chip.value);
              onSelectedReposChange?.(newSelectedRepos);
            }}
          />
        ))}
      </div>)}

      {/* Filter chips */}
      {selectedRepos && selectedRepos.length > 0 && filterChips.length > 0 && (
        <div className="filter-chips-container">
          <div className="filter-chips-header">
            <FILTER_ICON width='15' height='17' />
            <span>Filters:</span>
          </div>
          {filterChips.map((chip) => (
            <FilterChip
              key={chip.key}
              label={chip.label}
              value={chip.value}
              onClear={chip.onClear}
            />
          ))}
        </div>
      )}

      {/* Sort chips */}
      {sortChips.length > 0 && (
        <div className="filter-chips-container">
          <div className="filter-chips-header">
            <LiaSortSolid
              style={{
                fontSize: '18px',
                // marginTop: '20px',
                cursor: 'default',
                // opacity: 0.4,
              }}
            />
            <span>Sort by:</span>
          </div>
          {sortChips.map((chip) => (
            <FilterChip
              key={chip.key}
              label={chip.label}
              value={chip.value}
              icon={chip.icon}
              onClear={() => {
                // Handle clearing individual sort field
                if (Array.isArray(sortField)) {
                  const newSorts = sortField.filter(s => s.field !== chip.key);
                  if (newSorts.length === 0) {
                    onSortChange?.(null, null);
                  } else if (newSorts.length === 1) {
                    onSortChange?.(newSorts[0].field, newSorts[0].direction);
                  } else {
                    onSortChange?.(newSorts, 'multiple');
                  }
                } else {
                  onSortChange?.(null, null);
                }
              }}
            />
          ))}
        </div>
      )}

      {/* Global actions */}
      <div className="filters-actions" style={{ minHeight: '60px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        {(searchValue ||
          (selectedRepos && selectedRepos.length > 0) ||
          (repoFilters && Object.values(repoFilters).some(filters =>
            filters && typeof filters === "object"
              ? Object.values(filters).some(value => value)
              : filters
          ))
        ) && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" className="filters-btn" onClick={onReset}>
                Clear all
              </button>
              <button type="button" className="filters-btn" onClick={onApply} disabled={!canApply}>
                Search
              </button>
              {savedSearchValue && savedSearchValue !== 'new' && (
                <button type="button" className="filters-btn"
                  onClick={() => {
                    setShowDeleteAlert(true);
                    setSearchToDelete(selectedSearchData);
                  }}
                >
                  Delete
                </button>
              )}
              {savedSearchValue && savedSearchValue !== 'new' ? (
                <button type="button" className="filters-btn filters-btn-primary" onClick={handleSaveSearchModal} disabled={!canApply}>
                  Update & Search
                </button>
              ) :
                (isMaxQueriesReached && canApply) ? (
                  <BasicTooltip
                    text={`Maximum number of saved queries reached (${savedQueriesCount}/${maxNamedQueries}). Update or delete existing searches to save new ones.`}
                    placement="top"
                  >
                    <div style={{ display: 'inline-block' }}>
                      <button
                        type="button"
                        className="filters-btn filters-btn-primary"
                        disabled={isSaveDisabled}
                      >
                        Save & Search
                      </button>
                    </div>
                  </BasicTooltip>
                ) : (
                  <button type="button" className="filters-btn filters-btn-primary" onClick={handleSaveSearchModal} disabled={!canApply}>
                    Save & Search
                  </button>
                )}
            </div>
          )}
      </div>

      <SaveSearchModal
        show={showSaveSearchModal}
        onHide={() => setShowSaveSearchModal(false)}
        onSave={handleSaveSearch}
        initialName={selectedSearchData && savedSearchValue !== 'new' ? selectedSearchData.label : ''}
        isUpdate={savedSearchValue !== 'new'}
      />
      <AlertModal
        show={showDeleteAlert}
        onHide={handleDeleteCancel}
        saveButtonText='Delete'
        cancelButtonText='Cancel'
        title="Delete Saved Search"
        message={`Are you sure you want to delete "${searchToDelete?.label || 'this saved search'}"?`}
        handleConfirm={handleDeleteConfirm}
      />
    </div>
  );
};

export default CustomFilters;